import type { Styles } from '@adornis/ass/style.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import '@chancenwerk/client/components/chancen-component-icon-spoiler.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { Adresse } from '@chancenwerk/shared/adresse.js';
import '@chancenwerk/client/design/components/cr-date-picker-dropdown.js';
import '@chancenwerk/client/design/components/cr-dropdown-selection.js';
import { RaumRoutes } from '@chancenwerk/routes/raumRoutes.js';
import { PartnerAPI } from '@chancenwerk/shared/master-data-management/partner/api/PartnerAPI.js';
import { PartnerAspect } from '@chancenwerk/shared/master-data-management/partner/entities/aspects/PartnerAspect.js';
import { Partner } from '@chancenwerk/shared/master-data-management/partner/entities/Partner.js';
import { subscribeWorkshopAllAttendanceSimple } from '@chancenwerk/shared/workshops/api/subscribeWorkshopAllAttendanceSimple.js';
import { WorkshopAttendance } from '@chancenwerk/shared/workshops/entities/WorkshopAttendance.js';
import { WorkshopAttendanceStatus } from '@chancenwerk/shared/workshops/entities/WorkshopAttendanceStatus.js';
import { WorkshopFilter } from '@chancenwerk/shared/workshops/entities/WorkshopFilter.js';
import { WorkshopTrainingAttendance } from '@chancenwerk/shared/workshops/entities/WorkshopTrainingAttendance.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';

/**
 * @customElement akademie-component-workshop-rag-by-state
 */
@customElement('akademie-component-workshop-rag-by-state')
export class AkademieComponentWorkshopRAGByState extends ChemistryLitElement {
  @state() private readonly _workshopFilter = new RXController(
    this,
    new WorkshopFilter({ workshopID: '', hasState: true }),
  );

  override styles(): Styles[] {
    return [...super.styles(), { ':host': { overflow: 'auto' } }];
  }

  override render() {
    return html` <cr-card space="md" padding="md" ${css({ overflow: 'auto', maxWidth: '100%' })}>
      <chancen-component-card-title> je Status-Statistik </chancen-component-card-title>
      <cr-text bold>Filter</cr-text>
      ${this._filter()}
      <cr-text bold>Einträge</cr-text>
      ${this._table()}
    </cr-card>`;
  }

  private _filter() {
    return html` <cr-grid columns="1fr 1fr" space="md">
      <cr-dropdown-selection
        placeholder="Status"
        .selectables=${Object.values(WorkshopAttendanceStatus)}
        .value=${this._workshopFilter.value.state}
        @value-picked=${(e: ValueEvent<Maybe<WorkshopAttendanceStatus>>) => {
          this._workshopFilter.value.state = e.detail.value;
          this._workshopFilter.next(this._workshopFilter.value);
        }}
        clearable
      >
      </cr-dropdown-selection>
      <x-entity-picker
        flex
        placeholder="Schule"
        clearable
        .options=${{
          class: Partner,
          operation: PartnerAPI.subscribeByFilter,
          selectionSet: { name: 1, kuerzel: 1, address: Adresse.allFields },
          filter: new AdornisFilter({ searchFields: ['name', 'kuerzel'] }),
        }}
        .renderString=${(e: Maybe<Partner>) =>
          e
            ? `${e.kuerzel ?? 'Kein Kürzel angegeben'}${e.name ? ` / ${e.name} ` : ''} (${
                e.address?.toString()
                  ? e.address.toString()
                  : 'Keine Adresse für diesen Kooperationspartner hinterlegt!'
              })`
            : ''}
        @value-picked=${(e: ValueEvent<Maybe<string>>) => {
          this._workshopFilter.value.aspects = this._workshopFilter.value.aspects.filter(
            aspect => aspect.customID !== 'partnerID',
          );
          if (!e.detail.value) {
            return;
          }
          this._workshopFilter.value.aspects.push(
            new PartnerAspect({ customID: 'partnerID', value: [e.detail.value] }),
          );
        }}
      ></x-entity-picker>
      <cr-date-picker-dropdown
        .end-year=${DateTime.now().plus({ years: 10 }).year}
        placeholder="von"
        click-to-save
        flex
        .value=${this._workshopFilter.value.from}
        @value-picked=${(e: ValueEvent<DateTime>) => {
          this._workshopFilter.value.from = e.detail.value;
          if (!this._workshopFilter.value.till || this._workshopFilter.value.till < e.detail.value) {
            this._workshopFilter.value.till = e.detail.value.endOf('month');
          }
          this._workshopFilter.next(this._workshopFilter.value);
        }}
      ></cr-date-picker-dropdown>
      <cr-date-picker-dropdown
        .end-year=${DateTime.now().plus({ years: 10 }).year}
        placeholder="bis"
        click-to-save
        flex
        .value=${this._workshopFilter.value.till}
        @value-picked=${(e: ValueEvent<DateTime>) => {
          this._workshopFilter.value.till = e.detail.value;
          if (!this._workshopFilter.value.from || this._workshopFilter.value.from > e.detail.value) {
            this._workshopFilter.value.from = e.detail.value.startOf('month');
          }
          this._workshopFilter.next(this._workshopFilter.value);
        }}
      ></cr-date-picker-dropdown>
    </cr-grid>`;
  }

  private _table() {
    return html`
      <cr-data-view
        ${css({ overflowX: 'auto' })}
        .options=${{
          class: WorkshopTrainingAttendance,
          operation: subscribeWorkshopAllAttendanceSimple,
          view: new View<typeof WorkshopTrainingAttendance>({ filter: this._workshopFilter.value }),
          isColumnResizable: true,
          onlyShowLastTranslation: true,
          customColumns: [
            {
              columnName: '',
              customID: 'icons',
              columnWidth: 'max-content',
              selectionSet: { user: { vorname: 1, nachname: 1 }, partner: { name: 1 } },
              getFieldMapping: w =>
                html`<cr-flex horizontal space-between>
                  <cr-button
                    mode="icon"
                    icon="person"
                    href="${`${projectConfig.get('RAUM_URL')}${RaumRoutes.editEmployee.substring(1)}/${w.user?._id}`}"
                    target="_blank"
                  ></cr-button>
                  ${w instanceof WorkshopAttendance && w.partner?.name
                    ? html`<cr-button
                        mode="icon"
                        icon="school"
                        href="${`${projectConfig.get('RAUM_URL')}${RaumRoutes.editEmployee.substring(1)}/${
                          w.partner._id
                        }`}"
                        target="_blank"
                      >
                        <cr-tooltip><cr-text>${w.partner.name}</cr-text></cr-tooltip>
                      </cr-button>`
                    : nothing}
                </cr-flex>`,
            },
            {
              columnName: 'Name',
              customID: 'name',
              columnWidth: 'max-content',
              selectionSet: { user: { vorname: 1, nachname: 1, email: 1 } },
              getFieldMapping: w => w.user?.toString(),
            },
            {
              columnName: 'Workshop',
              columnWidth: 'max-content',
              customID: 'workshop',
              renderHeader: this._smallerHeader('Workshops'),
              selectionSet: { workshopID: 1, workshop: { title: 1 } },
              getFieldMapping: w => w.workshop?.title,
            },
            {
              columnName: 'Durchführungsdatum',
              columnWidth: '180px',
              customID: 'date',
              renderHeader: this._smallerHeader('Durchführungsdatum'),
              selectionSet: { workshopID: 1, date: 1 },
              getFieldMapping: w => w.date?.toLocaleString() ?? 'nicht eingetragen',
            },
            {
              columnName: 'Status',
              columnWidth: '1fr',
              customID: 'state',
              renderHeader: this._smallerHeader('Status'),
              selectionSet: { state: 1 },
              getFieldMapping: w => w.state,
            },
            {
              columnName: 'Schule',
              columnWidth: '1fr',
              customID: 'school',
              renderHeader: this._smallerHeader('Schule'),
              selectionSet: {
                user: {
                  vertraege: {
                    taetigkeiten: {
                      object: { produkt: { _class: 1, kuerzel: 1 }, kuerzel: 1, school: { name: 1, kuerzel: 1 } },
                    },
                  },
                },
              },
              getFieldMapping: w => {
                return [
                  ...new Set(w.user?.vertraege.flatMap(v => v.taetigkeiten?.map(t => t.object?.school?.name))),
                ].join(', ');
              },
            },
          ],
        } as DataViewOptions<typeof WorkshopTrainingAttendance>}
      >
      </cr-data-view>
    `;
  }

  private _smallerHeader(text: Renderable) {
    return () => {
      return html`<cr-text bold>${text}</cr-text>`;
    };
  }
}
